import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridCellRendererButtonsComponent } from '@shared/components/ag-grid-cell-renderer-buttons/ag-grid-cell-renderer-buttons.component';
import {
  ColumnWidth,
  DropDownColumns,
  Pagination,
  SortColumns,
  SortedColumns,
} from '@shared/models/shared.model';
import { GlobalLanguageService } from '@shared/services/global-language.service';
import {
  CellEditingStoppedEvent,
  CellKeyDownEvent,
  ColDef,
  Column,
  ColumnApi,
  ColumnResizedEvent,
  FullWidthCellKeyDownEvent,
  GridApi,
  GridReadyEvent,
  HeaderValueGetterParams,
  RowClickedEvent,
  RowNode,
  SortChangedEvent,
  ValueFormatterParams,
} from 'ag-grid-community';
import { Observable } from 'rxjs';
import { CustomButton } from '../ag-grid-cell-renderer-buttons/ag-grid-cell-renderer-buttons.model';
import { AgGridFloatingFilterComponent } from '../ag-grid-floating-filter/ag-grid-floating-filter.component';
import { CustomButtonConfig } from './list-grid.model';
import { CheckboxCellRendererComponent } from '../ag-grid-checkbox-cell-renderer/ag-grid-checkbox-cell-renderer.component';
import { Columns } from '@views/secure/devices/store/models/shared.model';
import { Permissions } from '@shared/constants/permission.constant';

@Component({
  selector: 'app-list-grid',
  templateUrl: './list-grid.component.html',
})
export class ListGridComponent implements OnInit, OnChanges, OnDestroy {
  @Input() rowData: Observable<any[]>;
  @Input() columnDefs: ColDef[];
  @Input() noRowsText: string;
  @Input() addNewUrl: string;
  @Input() disableAdd: boolean;
  @Input() editUrl: string;
  @Input() hasDelete = true;
  @Input() disableDelete: boolean;
  @Input() disableEdit: boolean;
  @Input() hasFloatingFilter: boolean;
  @Input() useOverlayNoRowsTemplate: boolean;
  @Input() isFloatingFilterEnabled: boolean;
  @Input() disableFilters = false;
  @Input() customButtonConfig: CustomButtonConfig[];
  @Input() rowHeight = 30;
  @Input() suppressRowClickSelection: boolean;
  @Input() hasPagination: boolean;
  @Input() paginationPageSize: number;
  @Input() pagination: Pagination;
  @Input() context: CustomButton[];
  @Input() overlayNoRowsTemplate: string;
  @Input()
  set defaultColDef(defaultColumnDef: ColDef) {
    this._defaultColDef = {
      ...this.customColDef,
      ...defaultColumnDef,
    };
  }
  @Input() hasGlobalSearch: boolean;
  @Input() hasGridSearch: boolean;
  @Input() globalSearch: string;
  @Input() hasColumnSelect: boolean = false;
  @Input() columnList: Columns[] = [];
  @Input() selectedColumns: Columns[] = [];
  @Input() permission: Permissions;

  @Output() private delete = new EventEmitter<string[]>();
  @Output() private selectionChanged = new EventEmitter<any[]>();
  @Output() private gridReady = new EventEmitter<GridReadyEvent>();
  @Output() private pageChange = new EventEmitter<number>();
  @Output() private columnResized = new EventEmitter<ColumnWidth[]>();
  @Output() private sortChanged = new EventEmitter<SortColumns[]>();
  @Output() private toggleFilter = new EventEmitter<boolean>();
  @Output() private rowClicked = new EventEmitter();
  @Output() private globalSearchSubmit = new EventEmitter<string>();
  @Output() private gridSearch = new EventEmitter<string>();
  @Output() private columnSelect = new EventEmitter<DropDownColumns[]>();
  @Output() private columnDeselect = new EventEmitter<DropDownColumns[]>();
  @Output() private columnReorder = new EventEmitter<SortedColumns[]>();
  @Output() private rowDataUpdated = new EventEmitter();
  @Output() private cellKeyDown = new EventEmitter<CellKeyDownEvent | FullWidthCellKeyDownEvent>();
  @Output() private cellEditingStopped = new EventEmitter<CellEditingStoppedEvent>();

  customColDef: ColDef;
  isAutoSize: boolean;
  gridSearchedValue: string;
  overlayLoadingTemplate: string;
  hasRows = false;
  private api: GridApi | undefined;
  private columnApi: ColumnApi;
  private debounceTimeout: ReturnType<typeof setTimeout>;
  private _defaultColDef: ColDef;

  components: {
    actionRenderer: typeof AgGridCellRendererButtonsComponent;
    floatingFilterComponent: typeof AgGridFloatingFilterComponent;
    checkBoxCellRenderer: typeof CheckboxCellRendererComponent;
  };

  constructor(
    private globalTranslate: GlobalLanguageService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.customColDef = {
      floatingFilterComponent: 'floatingFilterComponent',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      headerValueGetter: (param: HeaderValueGetterParams) => {
        if (param.colDef.headerName) {
          return this.globalTranslate.getTranslation(param.colDef.headerName);
        }
      },
      valueFormatter: (param: ValueFormatterParams) =>
        param.value === null || param.value === undefined || param.value === '' ? '-' : param.value,
      resizable: true,
      suppressMenu: true,
      suppressMovable: true,
    };
    this._defaultColDef = this.customColDef;
    this.components = {
      actionRenderer: AgGridCellRendererButtonsComponent,
      floatingFilterComponent: AgGridFloatingFilterComponent,
      checkBoxCellRenderer: CheckboxCellRendererComponent,
    };
  }

  get defaultColDef(): ColDef {
    return this._defaultColDef;
  }

  ngOnInit(): void {
    const translatedNoRowsText = this.globalTranslate.getTranslation(this.overlayNoRowsTemplate);
    this.overlayNoRowsTemplate = `<span
      class="badge"
      style="font-size: inherit; font-weight: normal; padding: 0.5em 1em; background-color: #cfe2ff; color: #084298"
      ><i class="fa fa-inbox mr-2" aria-hidden="true"></i>${translatedNoRowsText}</span
    >`;

    const translatedLoadingText = this.globalTranslate.getTranslation('SHARED.FETCHING_DATA_TEXT');
    this.overlayLoadingTemplate = `
      <div class="ag-custom-loading d-flex align-items-center justify-content-center">
        <i class="fa fa-spinner fa-spin text-primary fa-3x mr-2"></i>
        <span>${translatedLoadingText}</span>
      </div>
    `;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columnDefs'] && !changes['columnDefs'].firstChange && this.api) {
      this.api.stopEditing();
      this.api.setColumnDefs(this.columnDefs);
      this.api.refreshCells({
        force: true,
      });
      this.api.redrawRows();
    }
  }

  ngOnDestroy(): void {
    this.destroyGrid();
  }

  onGridReady(param: GridReadyEvent): void {
    this.api = param.api;
    this.columnApi = param.columnApi;
    this.hasRows = !!this.api.getDisplayedRowCount();
    this.gridReady.emit(param);
  }

  private destroyGrid() {
    if (this.api) {
      this.api.destroy();
      this.api = null;
    }
  }

  onColumnResized(event: ColumnResizedEvent): void {
    const updatedColumns: ColumnWidth[] = [];
    event.columns.forEach((column: Column) => {
      updatedColumns.push({ field: column.getId(), width: column.getActualWidth() });
    });
    this.columnResized.emit(updatedColumns);
  }

  onPageChange(page: number): void {
    this.pageChange.emit(page);
  }

  onSelectionChanged(): void {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    this.debounceTimeout = setTimeout(() => {
      this.selectionChanged.emit(this.api.getSelectedRows());
    }, 100);
  }

  onRowClicked(params: RowClickedEvent): void {
    this.rowClicked.emit(params);
  }

  onEdit(): void {
    if (this.noOfSelectedRows === 1) {
      const selectedId = this.api.getSelectedNodes()[0].data.id;
      this.router.navigate([this.editUrl, selectedId], { relativeTo: this.route });
    }
  }

  onDelete(): void {
    if (!this.isDeleteDisabled) {
      const selectedIds: string[] = this.api
        .getSelectedNodes()
        .map((rowData: RowNode) => rowData.data.id);
      this.delete.emit(selectedIds);
    }
  }

  get noOfSelectedRows(): number {
    return this.api?.getSelectedNodes()?.length;
  }

  get isDeleteDisabled(): boolean {
    return !this.noOfSelectedRows || this.disableDelete;
  }

  public onSortChanged(event: SortChangedEvent): void {
    const sortedColumn = event.columnApi
      .getColumnState()
      .filter((col) => col.sort)
      .map((col) => ({ colId: col.colId, sort: col.sort }));
    this.sortChanged.emit(sortedColumn);
  }

  public onToggleFloatingFilter(): void {
    this.isFloatingFilterEnabled = !this.isFloatingFilterEnabled;
    this.toggleFilter.emit(this.isFloatingFilterEnabled);
  }

  public onChangeColumnSize(): void {
    this.isAutoSize ? this.sizeColumnsToFit() : this.autoSizeAllColumns();
  }

  private sizeColumnsToFit(): void {
    this.api?.sizeColumnsToFit();
    this.isAutoSize = false;
  }

  private autoSizeAllColumns(): void {
    this.columnApi?.autoSizeAllColumns();
    this.isAutoSize = true;
  }

  public isLoading(isLoading: (() => boolean) | undefined): boolean {
    return isLoading === undefined ? false : isLoading();
  }

  public onGlobalSearchChange(value: string): void {
    this.globalSearch = value;
  }

  public onGlobalSearchSubmit(): void {
    this.globalSearchSubmit.emit(this.globalSearch);
  }

  public onGridSearch(): void {
    this.gridSearch.emit(this.gridSearchedValue);
  }

  public onColumnSelect(item: DropDownColumns): void {
    this.columnSelect.emit([item]);
  }

  public onAllColumnsSelect(items: DropDownColumns[]): void {
    this.columnSelect.emit(items);
  }

  public onColumnDeSelect(item: DropDownColumns): void {
    this.columnDeselect.emit([item]);
  }

  public onColumnReorder(reOrderedItems: SortedColumns[]): void {
    this.columnReorder.emit(reOrderedItems);
  }

  public onRowDataUpdate(): void {
    this.hasRows = !!this.api?.getDisplayedRowCount();
    this.rowDataUpdated.emit();
  }

  public onCellKeyDown(event: CellKeyDownEvent | FullWidthCellKeyDownEvent): void {
    this.cellKeyDown.emit(event);
  }

  public onCellEditingStopped(event: CellEditingStoppedEvent): void {
    this.cellEditingStopped.emit(event);
  }
}
